import React, { useState } from 'react';
import { EmailRecipientOption } from './getEmailTemplateRecipients';
import { useTranslation } from 'react-i18next';

interface EmailRecipientInputProps {
    recipients: EmailRecipientOption[]
    placeholder?: string
    isInvalid?: boolean
    onChange: (recipients: EmailRecipientOption[]) => void
}

const isValidEmail = (value: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

const EmailRecipientInput: React.FC<EmailRecipientInputProps> = ({
    recipients,
    placeholder,
    isInvalid,
    onChange
}) => {
    const { t } = useTranslation();
    const [inputValue, setInputValue] = useState('')

    const addEmail = (value: string) => {
        const emailValues = value
            .split(/[,\s]+/)
            .map(email_value => email_value.trim().toLowerCase())
            .filter(email_value => email_value)

        const nextRecipients = [...recipients]

        emailValues.forEach(email_value => {
            const alreadyExists = nextRecipients.some(recipient => recipient.email.toLowerCase() === email_value)

            if (isValidEmail(email_value) && !alreadyExists) {
                nextRecipients.push({ email: email_value })
            }
        })

        onChange(nextRecipients)
        setInputValue('')
    }

    const removeEmail = (email: string) => {
        const nextRecipients = recipients.filter(recipient => recipient.email !== email)
        onChange(nextRecipients)
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' || event.key === ',' || event.key === 'Tab' || event.key === ' ') {
            if (inputValue.trim()) {
                event.preventDefault()
                addEmail(inputValue)
            }
        }

        if (event.key === 'Backspace' && !inputValue && recipients.length > 0) {
            const nextRecipients = recipients.slice(0, -1)
            onChange(nextRecipients)
        }
    }

    const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
        const pastedValue = event.clipboardData.getData('text')

        if (pastedValue.includes(',') || pastedValue.includes(' ')) {
            event.preventDefault()
            addEmail(pastedValue)
        }
    }

    const handleBlur = () => {
        if (inputValue.trim()) {
            addEmail(inputValue)
        }
    }

    return (
        <div className={`email-recipient-input ${isInvalid ? 'invalid' : ''}`}>
            {recipients.map(recipient => (
                <span 
                    key={recipient.email}
                    className='email-recipient-chip'
                >
                    <span>
                        {recipient.email}
                        {recipient.label && (
                            <span className='email-recipient-chip-label'>
                                {' '}({t(`email.email_template.send_to.${recipient.label}`)})
                            </span>
                        )}
                    </span>
                    <button
                        type='button'
                        className='email-recipient-chip-remove'
                        onClick={() => removeEmail(recipient.email)}
                    >
                        ×
                    </button>
                </span>
            ))}

            <input
                value={inputValue}
                placeholder={recipients.length === 0 ? placeholder : ''}
                onChange={event => setInputValue(event.target.value)}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                onBlur={handleBlur}
                autoComplete='off'
                autoCorrect='off'
                autoCapitalize='none'
                spellCheck={false}
                data-1p-ignore='true'
                data-lpignore='true'
                data-form-type='other'
            />
        </div>
    )
}

export default EmailRecipientInput